import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Paper,
  FormControlLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const TAB_COLORS = {
  all: { bg: '#DBEAFE', border: '#3B82F6', text: '#1E3A8A' },
  recent: { bg: '#A7F3D0', border: '#10B981', text: '#065F46' },
  old: { bg: '#FDE68A', border: '#F59E0B', text: '#78350F' },
  veryOld: { bg: '#FECACA', border: '#EF4444', text: '#7F1D1D' },
};

const ROLE_COLORS = {
  Frontend: { bg: '#7DD3FC', text: '#0C4A6E' },
  Backend: { bg: '#C4B5FD', text: '#4C1D95' },
  Database: { bg: '#FDE68A', text: '#78350F' },
  'Database backup': { bg: '#FBCFE8', text: '#9D174D' },
  'Email extractor': { bg: '#A7F3D0', text: '#065F46' },
  'Video creator': { bg: '#FDBA74', text: '#7C2D12' },
  'Customization API': { bg: '#D9F99D', text: '#365314' },
  Training: { bg: '#A5B4FC', text: '#312E81' },
  Jitsi: { bg: '#DDD6FE', text: '#5B21B6' },
  Other: { bg: '#E5E7EB', text: '#111827' },
};

function RoleChip({ role }) {
  const c = ROLE_COLORS[role] || ROLE_COLORS.Other;
  return (
    <Chip
      size="small"
      label={role || 'Other'}
      sx={{ bgcolor: c.bg, color: c.text, fontWeight: 700, border: '1px solid', borderColor: alpha(c.text, 0.25) }}
    />
  );
}

function BucketPanel({ title, subtitle, colorKey, images, selected, onToggle, onToggleAll, onView, onAskDelete, allowBulkDelete }) {
  const color = TAB_COLORS[colorKey];
  const deletable = images.filter((i) => i.canDeleteDefault || !i.protected);
  const allSelected = deletable.length > 0 && deletable.every((i) => selected.has(i.name));

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        border: `2px solid ${color.border}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: alpha(color.bg, 0.45),
      }}
    >
      <Box sx={{ px: 2, py: 1.25, bgcolor: color.bg, borderBottom: `2px solid ${color.border}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
          <Box>
            <Typography sx={{ fontWeight: 800, color: color.text }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: color.text, opacity: 0.85 }}>
              {subtitle}
            </Typography>
          </Box>
          {allowBulkDelete && deletable.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                control={<Checkbox size="small" checked={allSelected} onChange={() => onToggleAll(deletable)} />}
                label={<Typography variant="body2" fontWeight={700}>Mark all</Typography>}
              />
              <Button
                size="small"
                color="error"
                variant="contained"
                startIcon={<DeleteIcon />}
                disabled={[...selected].filter((n) => deletable.some((i) => i.name === n)).length === 0}
                onClick={() => onAskDelete([...selected].filter((n) => deletable.some((i) => i.name === n)), false)}
              >
                Delete marked
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            {allowBulkDelete && <TableCell padding="checkbox" />}
            <TableCell>Image name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Size</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {images.length === 0 && (
            <TableRow>
              <TableCell colSpan={allowBulkDelete ? 7 : 6}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No images in this group.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {images.map((img) => (
            <TableRow key={img.id} hover>
              {allowBulkDelete && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    disabled={img.protected && !img.canDeleteDefault}
                    checked={selected.has(img.name)}
                    onChange={() => onToggle(img)}
                  />
                </TableCell>
              )}
              <TableCell>
                <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>
                  {img.name}
                </Typography>
                {img.protected && (
                  <Chip size="small" label="Protected (top 3)" color="success" sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }} />
                )}
              </TableCell>
              <TableCell><RoleChip role={img.role} /></TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={800} sx={{ fontFamily: 'ui-monospace, monospace' }}>
                  {img.version || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {img.createdAt ? new Date(img.createdAt).toLocaleString() : '—'}
                </Typography>
              </TableCell>
              <TableCell>{img.sizeLabel || '—'}</TableCell>
              <TableCell align="right">
                <Tooltip title="View image contents (inspect + layers)">
                  <IconButton size="small" onClick={() => onView(img)}>
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={img.protected ? 'Protected — override required' : 'Delete image'}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onAskDelete([img.name], img.protected)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function ImagesAdmin() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRefs, setDeleteRefs] = useState([]);
  const [needsOverride, setNeedsOverride] = useState(false);
  const [overrideAck, setOverrideAck] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/images');
      setPayload(res.data?.data || null);
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to load images');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const images = payload?.images || [];
  const buckets = payload?.buckets || { recent: [], old: [], veryOld: [] };

  const toggle = (img) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(img.name)) next.delete(img.name);
      else next.add(img.name);
      return next;
    });
  };

  const toggleAll = (list) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = list.every((i) => next.has(i.name));
      list.forEach((i) => {
        if (allOn) next.delete(i.name);
        else next.add(i.name);
      });
      return next;
    });
  };

  const openView = async (img) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await api.get('/admin/images/detail', { params: { ref: img.name } });
      setDetail(res.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to inspect image');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const askDelete = (refs, protectedHit) => {
    const unique = [...new Set(refs.filter(Boolean))];
    if (!unique.length) return;
    const anyProtected = protectedHit || unique.some((name) => images.find((i) => i.name === name)?.protected);
    setDeleteRefs(unique);
    setNeedsOverride(Boolean(anyProtected));
    setOverrideAck(false);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (needsOverride && !overrideAck) {
      toast.warning('Confirm the override checkbox to delete a protected (top 3) image.');
      return;
    }
    setDeleting(true);
    try {
      const res = await api.post('/admin/images/delete', {
        refs: deleteRefs,
        overrideProtected: needsOverride && overrideAck,
      });
      const data = res.data?.data;
      const failed = (data?.results || []).filter((r) => !r.ok);
      if (data?.deleted) toast.success(`Deleted ${data.deleted} image(s)`);
      failed.forEach((f) => toast.error(`${f.ref}: ${f.message}`, { autoClose: 10000 }));
      setDeleteOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const tabMeta = useMemo(
    () => [
      { key: 'all', label: `All images (${images.length})` },
      { key: 'recent', label: `Recent (${buckets.recent?.length || 0})` },
      { key: 'old', label: `Old (${buckets.old?.length || 0})` },
      { key: 'veryOld', label: `Very old (${buckets.veryOld?.length || 0})` },
    ],
    [images.length, buckets]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Images Administration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Local Docker images for ERP4C (frontend, backend, sidecars). Sorted by created time.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && payload && !payload.dockerAvailable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {payload.message ||
            'Image inventory is not available. On docker-desktop-k8s-local run sync-image-inventory.sh on your Mac, enable backend.imageAdmin, and redeploy.'}
        </Alert>
      )}

      {!loading && payload?.message && payload?.dockerAvailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {payload.message}
          {payload.syncedAt ? ` Last sync: ${new Date(payload.syncedAt).toLocaleString()}.` : ''}
        </Alert>
      )}

      {!loading && payload?.dockerAvailable && (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 2,
              minHeight: 48,
              '& .MuiTab-root': { fontWeight: 800, textTransform: 'none', minHeight: 48, borderRadius: 1.5, mx: 0.5 },
            }}
          >
            {tabMeta.map((t, idx) => {
              const c = TAB_COLORS[t.key];
              return (
                <Tab
                  key={t.key}
                  label={t.label}
                  sx={{
                    bgcolor: tab === idx ? c.bg : alpha(c.bg, 0.35),
                    color: `${c.text} !important`,
                    border: `2px solid ${c.border}`,
                  }}
                />
              );
            })}
          </Tabs>

          {(tab === 0 || tab === 1) && (
            <BucketPanel
              title="Recent Images / Important"
              subtitle="First 3 latest images. Deletion blocked unless you override (cannot be retrieved after delete)."
              colorKey="recent"
              images={buckets.recent || []}
              selected={selected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              onView={openView}
              onAskDelete={askDelete}
              allowBulkDelete={false}
            />
          )}
          {(tab === 0 || tab === 2) && (
            <BucketPanel
              title="Old Images / Can be marked for Deletion"
              subtitle="4th through 6th newest. Mark all and delete when ready."
              colorKey="old"
              images={buckets.old || []}
              selected={selected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              onView={openView}
              onAskDelete={askDelete}
              allowBulkDelete
            />
          )}
          {(tab === 0 || tab === 3) && (
            <BucketPanel
              title="Very Old Images / Needed to be Deleted"
              subtitle="7th and older. Prefer cleanup."
              colorKey="veryOld"
              images={buckets.veryOld || []}
              selected={selected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              onView={openView}
              onAskDelete={askDelete}
              allowBulkDelete
            />
          )}

          <Alert severity="info" sx={{ mt: 1 }}>
            <strong>Format:</strong> <code>MAJOR.YYYY.WW.MINOR</code> (local builds may append a short suffix).
            Deleting removes the image from the local Docker image store only — rebuild or pull again to restore.
          </Alert>
        </>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Image contents / inspect</DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!detailLoading && detail && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {detail.note}
              </Typography>
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' }}>
                <div><strong>Ref:</strong> {detail.ref}</div>
                <div><strong>Id:</strong> {detail.inspect?.id}</div>
                <div><strong>Created:</strong> {detail.inspect?.created}</div>
                <div><strong>Size:</strong> {detail.inspect?.sizeLabel}</div>
                <div><strong>OS/Arch:</strong> {detail.inspect?.os}/{detail.inspect?.architecture}</div>
                <div><strong>Cmd:</strong> {(detail.inspect?.cmd || []).join(' ') || '—'}</div>
                <div><strong>Entrypoint:</strong> {(detail.inspect?.entrypoint || []).join(' ') || '—'}</div>
              </Box>
              <Typography fontWeight={800}>Layer history</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Created</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Created by</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail.history || []).slice(0, 40).map((h, idx) => (
                    <TableRow key={`${h.id}-${idx}`}>
                      <TableCell>{h.created ? new Date(h.created).toLocaleString() : '—'}</TableCell>
                      <TableCell>{h.sizeLabel || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}>
                        {h.createdBy || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(detail.inspect?.env || []).length > 0 && (
                <>
                  <Typography fontWeight={800}>Environment (build-time)</Typography>
                  <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, fontSize: '0.72rem', overflow: 'auto', maxHeight: 200 }}>
                    {(detail.inspect.env || []).join('\n')}
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirm image deletion
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            Once deleted, these images <strong>cannot be retrieved</strong> from Docker. You must rebuild or pull them again.
          </Alert>
          {needsOverride && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              One or more selected images are in <strong>Recent Images / Important</strong> (top 3).
              They cannot be deleted by default, but you can <strong>override</strong> that decision below.
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            Images to delete:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {deleteRefs.map((r) => (
              <li key={r}>
                <Typography component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' }}>
                  {r}
                </Typography>
              </li>
            ))}
          </Box>
          {needsOverride && (
            <FormControlLabel
              sx={{ mt: 2 }}
              control={<Checkbox checked={overrideAck} onChange={(e) => setOverrideAck(e.target.checked)} />}
              label="I understand this is a protected recent image and I override the deletion block"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting || (needsOverride && !overrideAck)}
            onClick={confirmDelete}
          >
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
